/**
 * Files attached to transactions (docs/attachments-plan.md). The ledger holds
 * what each is — a row of `attachments` — and its bytes live beside the
 * ledger, wherever the ledger is, behind the same calls:
 *   a readable ledger of this device   IndexedDB `okane-files`, keyed `<ledger id>/<id>`
 *   a readable cloud ledger            the server's files of the org (tenantdb's
 *                                      files API), nothing kept on the device
 *   an encrypted cloud ledger          sealed on the device under a key of the file's
 *                                      own (in its row, which travels encrypted) and
 *                                      sent sealed; the device keeps, sealed, the files
 *                                      it attached or opened, and an upload queue
 *                                      (`okane.outbox`) for those not sent yet
 *   a ledger encrypted on this device  sealed the same way, kept on the device only —
 *                                      the keys are in rows that are never on disk
 *                                      readable — so moving it to the cloud and back
 *                                      carries the same sealed bytes
 *
 * The bytes are written before the row, so a row never names a file that was
 * never there; a file whose row is gone (a transaction deleted, its rows taken
 * along by the foreign key) is swept away (`sweep`). Every read checks the
 * file against its hash.
 */
import { insert, query, remove, run } from '../db'
import { uid } from '../lib/id'
import { sha256Hex, vet } from '../lib/attachments'
import { dataChanged } from './live'
import { currentLedger } from './ledgers'
import { currentState } from './ledger-states'
import * as cloud from './cloud'
import { state } from './state'
import { count } from './usage'
import { crypto as c } from 'tenantdb/sync'

// ------------------------------------------------------------------ where the bytes are

function idbFiles() {
  const open = () =>
    new Promise((resolve, reject) => {
      const req = indexedDB.open('okane-files', 1)
      req.onupgradeneeded = () => req.result.createObjectStore('files')
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  const ask = async (mode, fn) => {
    const db = await open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction('files', mode)
      const req = fn(tx.objectStore('files'))
      tx.oncomplete = () => resolve(req?.result)
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  }
  return {
    put: (ledger, id, blob) => ask('readwrite', (s) => s.put(blob, `${ledger}/${id}`)),
    get: async (ledger, id) => (await ask('readonly', (s) => s.get(`${ledger}/${id}`))) ?? null,
    remove: (ledger, id) => ask('readwrite', (s) => s.delete(`${ledger}/${id}`)),
    list: async (ledger) => {
      const keys = await ask('readonly', (s) => s.getAllKeys(IDBKeyRange.bound(`${ledger}/`, `${ledger}/\uffff`)))
      return keys.map((key) => ({ id: key.slice(ledger.length + 1) }))
    },
  }
}

/** The org's files on the server: fetched when opened, never kept here. */
function cloudFiles() {
  return {
    put: async (ledger, id, blob) => cloud.files.put(ledger, id, new Uint8Array(await blob.arrayBuffer())),
    get: async (ledger, id) => {
      try {
        return new Blob([await cloud.files.get(ledger, id)])
      } catch (err) {
        if (err?.code === 'attachment-missing') return null
        throw err
      }
    },
    remove: (ledger, id) => cloud.files.remove(ledger, id),
    list: async (ledger) => (await cloud.files.list(ledger)).files.map((f) => ({ id: f.id, size: f.size, modified: Date.parse(f.modified) })),
    usage: (ledger) => cloud.files.list(ledger),
  }
}

const overrides = {}
const kept = {}
/** The device's store, and the server's. */
const device = () => overrides.device ?? (kept.device ??= idbFiles())
const server = () => overrides.server ?? (kept.server ??= cloudFiles())
/** Where a readable ledger's bytes are: on the device, or on the server. */
const store = () => (currentState() === 'cloud' ? server() : device())
/** Other places for the bytes — in-memory ones, for the tests. One for both, or `{ device, server }`. */
export const setFileStore = (other) => Object.assign(overrides, other && !other.put ? other : { device: other, server: other })

// ------------------------------------------------------------------ what can be done here

const where = () => currentState()
/** An encrypted ledger, here or in the cloud: its files are sealed, a key for each. */
const encrypted = () => where() === 'vault' || where() === 'sealed'
/** One whose files also go to the server. */
const sealedHere = () => where() === 'vault'
const inCloud = () => where() === 'cloud' || where() === 'vault'
const role = () => (sealedHere() ? (state.sync.role ?? state.cloud.org?.role) : state.cloud.org?.role)

/** Whether this ledger's files can be seen here: any ledger open here. */
export const canSee = () => ['local', 'sealed', 'cloud', 'vault'].includes(where())
/** Whether files can be attached here too: not by a viewer of a cloud ledger. */
export const canAttach = () => canSee() && !(inCloud() && role() === 'viewer')
const ledgerId = () => (where() === 'cloud' || where() === 'vault' ? cloud.getOrgId() : currentLedger().id)
/**
 * Where on the device the open ledger's files are: a readable ledger's under
 * its id, an encrypted cloud ledger's (those kept here) under the org's, and a
 * ledger encrypted on this device's under `sealed:<id>` — a place of its own,
 * so moving between readable and encrypted here never writes over a file
 * before the move is done.
 */
export const sealedSpace = (id) => `sealed:${id}`
const space = () => (where() === 'sealed' ? sealedSpace(ledgerId()) : ledgerId())

export const attachmentsOf = (transactionId) =>
  query('SELECT * FROM attachments WHERE transaction_id = ? ORDER BY created_at, id', [transactionId])

/** How many files the open ledger has attached. */
export async function attachmentCount() {
  const [row] = await query('SELECT COUNT(*) AS n FROM attachments')
  return Number(row?.n ?? 0)
}

/**
 * Reads a picked file and checks it may be attached (an image or a PDF, small
 * enough): `{ name, type, size, bytes }`. Throws `attachment-type` / `attachment-too-big`.
 */
export async function readPicked(file) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  const { type } = vet(bytes)
  return { name: file.name || 'file', type, size: bytes.byteLength, bytes }
}

// ------------------------------------------------------------------ sealed files (an encrypted ledger)

/** What a sealed file is bound to: its org and its id — a file swapped for another does not open. */
const fileAad = (org, id) => `okane file ${org} ${id}`

const fail = (code, message) => Object.assign(new Error(message), { code })

/**
 * The upload queue: files kept here and not on the server yet. An entry is
 * `<org>/<id>` — an encrypted cloud ledger's file, kept under the org's id — or
 * `<place>><org>/<id>`, one waiting in another place on the device (a file
 * decrypted for a ledger decrypted in place), deleted from there once sent.
 */
const OUTBOX = 'okane.outbox'
const entry = (key) => {
  const [from, rest] = key.includes('>') ? key.split('>') : [null, key]
  const [org, id] = rest.split('/')
  return { from: from ?? org, org, id, staged: !!from }
}
function outbox() {
  try {
    return JSON.parse(localStorage.getItem(OUTBOX) ?? '[]')
  } catch {
    return []
  }
}
function setOutbox(list) {
  try {
    localStorage.setItem(OUTBOX, JSON.stringify(list))
  } catch {
    // Then it is only as long as the page: the file is still sealed on the device.
  }
  state.sync.outbox = list.filter((k) => entry(k).org === ledgerIdSafe()).length
}
const ledgerIdSafe = () => {
  try {
    return ledgerId()
  } catch {
    return ''
  }
}
/** Whether an attachment is still waiting to go up from this device. */
export const waiting = (id) => outbox().some((k) => entry(k).org === ledgerIdSafe() && entry(k).id === id)
/** Queues files kept on the device for the server — `from` the place they are in, if not the org's own. */
export function queueUpload(org, ids, from = null) {
  const keys = ids.map((id) => (from ? `${from}>${org}/${id}` : `${org}/${id}`))
  setOutbox([...outbox().filter((k) => !keys.includes(k)), ...keys])
}

let flushing = null
/**
 * Sends what is waiting, oldest first, and stops at the first that cannot go
 * (offline, signed out): the queue keeps it, and the next flush tries again —
 * after each attach, when sync is back up to date, and when the browser is
 * online again. One flush at a time; `flushing` is cleared only once it is
 * set (an empty queue finishes at once — clearing inside it would come first).
 */
export function flushOutbox() {
  if (!inCloud()) return Promise.resolve(0)
  // Known to be offline: nothing to try until the 'online' event.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve(0)
  if (flushing) return flushing
  flushing = sendWaiting().finally(() => {
    flushing = null
  })
  return flushing
}

async function sendWaiting() {
  let sent = 0
  try {
    for (const key of outbox()) {
      const { from, org, id, staged } = entry(key)
      if (org !== ledgerId()) continue
      const blob = await device().get(from, id)
      if (blob) await server().put(org, id, blob)
      setOutbox(outbox().filter((k) => k !== key))
      // A file only waiting here to be sent goes once it is.
      if (staged) await device().remove(from, id)
      sent++
    }
    state.sync.outboxError = null
  } catch (err) {
    // Offline, or the server said no: left in the queue for the next try.
    state.sync.outboxError = err?.code === 'offline' ? null : (err?.code ?? err?.message ?? String(err))
  } finally {
    setOutbox(outbox())
  }
  return sent
}
if (typeof window !== 'undefined') window.addEventListener('online', () => flushOutbox())

async function attachSealed(row, picked) {
  const org = ledgerId()
  const key = c.newKey()
  const sealed = await c.seal(key, picked.bytes, fileAad(org, row.id))
  row.file_key = c.toBase64(key)
  // Sealed and kept here first — and in the cloud, queued: it goes up when it can.
  await device().put(space(), row.id, new Blob([sealed]))
  const queued = sealedHere()
  if (queued) setOutbox([...outbox(), `${org}/${row.id}`])
  try {
    await insert('attachments', row)
  } catch (err) {
    if (queued) setOutbox(outbox().filter((k) => k !== `${org}/${row.id}`))
    await device().remove(space(), row.id).catch(() => {})
    throw err
  }
  if (queued) flushOutbox()
}

async function openSealed(row) {
  const org = ledgerId()
  // (A ledger encrypted here kept its files under its id before it had a place of its own.)
  let sealed = (await device().get(space(), row.id)) ?? (where() === 'sealed' ? await device().get(org, row.id) : null)
  if (sealed) sealed = new Uint8Array(await sealed.arrayBuffer())
  else if (!sealedHere()) throw fail('attachment-missing', 'This file is not on this device.')
  else {
    try {
      sealed = new Uint8Array(await (await server().get(org, row.id))?.arrayBuffer?.() ?? [])
    } catch (err) {
      throw err?.code === 'offline' ? fail('attachment-offline', 'This file is not on this device, and the server cannot be reached.') : err
    }
    // Not on the server yet: the device that attached it has not sent it.
    if (!sealed.length) throw fail('attachment-pending', 'This file is still on its way from the device that attached it.')
    // Opened here: kept here, sealed, so it opens again offline.
    await device().put(space(), row.id, new Blob([sealed]))
  }
  if (!row.file_key) throw fail('attachment-damaged', 'This file is damaged.')
  try {
    return await c.open(c.fromBase64(row.file_key), sealed, fileAad(org, row.id))
  } catch {
    throw fail('attachment-damaged', 'This file is damaged.')
  }
}

// ------------------------------------------------------------------ attaching, opening, removing

/** Attaches a read file (`readPicked`) to a transaction: the bytes first, then the row. */
export async function attach(transactionId, picked) {
  const row = {
    id: uid('at'),
    transaction_id: transactionId,
    name: picked.name,
    type: picked.type,
    size: picked.size,
    sha256: await sha256Hex(picked.bytes),
    created_at: new Date().toISOString(),
  }
  if (encrypted()) await attachSealed(row, picked)
  else {
    await store().put(ledgerId(), row.id, new Blob([picked.bytes], { type: picked.type }))
    try {
      await insert('attachments', row)
    } catch (err) {
      await store().remove(ledgerId(), row.id).catch(() => {})
      throw err
    }
  }
  count('attach', picked.type === 'application/pdf' ? 'pdf' : 'image')
  dataChanged()
  return row
}

/**
 * The file of an attachment row, checked against its hash. Throws
 * `attachment-missing` / `attachment-damaged`, and for an encrypted ledger
 * `attachment-pending` (not sent yet) / `attachment-offline`.
 */
export async function fileOf(row) {
  let bytes
  if (encrypted()) bytes = await openSealed(row)
  else {
    const blob = await store().get(ledgerId(), row.id)
    if (!blob) throw fail('attachment-missing', 'This file is not on this device.')
    bytes = new Uint8Array(await blob.arrayBuffer())
  }
  if ((await sha256Hex(bytes)) !== row.sha256) throw fail('attachment-damaged', 'This file is damaged.')
  return new Blob([bytes], { type: row.type })
}

/** Removes an attachment: the row, then its bytes — here, and on the server when it can. */
export async function detach(row) {
  await remove('attachments', row.id)
  if (sealedHere()) {
    const org = ledgerId()
    setOutbox(outbox().filter((k) => !(entry(k).org === org && entry(k).id === row.id)))
    await device().remove(org, row.id)
    // Offline, it stays there until an owner's device sweeps it.
    await server().remove(org, row.id).catch(() => {})
  } else if (where() === 'sealed') await device().remove(space(), row.id)
  else await store().remove(ledgerId(), row.id)
  dataChanged()
}

/** Deletes every file this device keeps for a ledger — one that is gone from here. */
export async function dropDeviceFiles(ledger) {
  for (const f of await device().list(ledger)) await device().remove(ledger, f.id)
  setOutbox(outbox().filter((k) => entry(k).from !== ledger))
}

/** A file no row names is left this long in the cloud: another device's row may be on its way. */
export const SWEEP_AFTER_MS = 24 * 60 * 60 * 1000

/**
 * Deletes the bytes no row names any more — a deleted transaction's, whose
 * rows went with it. On this device, at once (but never one still waiting to
 * go up). In the cloud, only an owner's device sweeps the server, and only
 * files a day old: a file is put before its row is written, and another
 * device's upload — or its row — may be on the way.
 */
export async function sweep({ now = Date.now() } = {}) {
  if (!canSee()) return 0
  const org = ledgerId()
  const named = new Set((await query('SELECT id FROM attachments')).map((r) => r.id))
  let gone = 0
  if (where() !== 'cloud') {
    const here = space()
    const queued = new Set(outbox().filter((k) => entry(k).from === here).map((k) => entry(k).id))
    for (const f of await device().list(here)) {
      if (named.has(f.id) || queued.has(f.id)) continue
      await device().remove(here, f.id)
      gone++
    }
  }
  if (inCloud() && role() === 'owner') {
    for (const f of await server().list(org)) {
      if (named.has(f.id) || (f.modified && now - f.modified <= SWEEP_AFTER_MS)) continue
      await server().remove(org, f.id)
      gone++
    }
  }
  return gone
}

/**
 * What the files take: `{ used, limit }` — the server's count and limit in the
 * cloud, the rows' sizes on this device (no limit but the browser's).
 */
export async function filesUsage() {
  if (!canSee()) return null
  if (inCloud() && !overrides.server) {
    const { used, limit } = await server().usage(ledgerId())
    return { used, limit }
  }
  const [row] = await query('SELECT COALESCE(SUM(size), 0) AS used FROM attachments')
  return { used: Number(row?.used ?? 0), limit: null }
}

// ------------------------------------------------------------------ files moving with a ledger
//
// Each move of the square carries the ledger's files (stores/ledger-states.js).
// These take their places explicitly — the ledger being moved is not always the
// one open — and check every file as it lands: a readable one against its hash,
// a sealed one by opening it with its row's key. A file a ledger names but that
// was never here (missing already) is passed over. `onProgress({ done, total })`
// says how far along it is. Nothing here deletes what it copied from: the move
// does that, once all of it has arrived.

/** What a ledger's files are, from its database (`query`, or a driver's `all`). */
export const fileRows = (ask = query) => ask('SELECT id, sha256, size, type, file_key FROM attachments ORDER BY created_at, id')

const bytesOf = async (blob) => new Uint8Array(await blob.arrayBuffer())
async function checked(bytes, row) {
  if ((await sha256Hex(bytes)) !== row.sha256) throw fail('attachment-damaged', 'A file is damaged: it is not what was attached.')
  return bytes
}
async function opened(sealed, row, org) {
  try {
    return await checked(await c.open(c.fromBase64(row.file_key), sealed, fileAad(org, row.id)), row)
  } catch (err) {
    throw err?.code === 'attachment-damaged' ? err : fail('attachment-damaged', 'A file is damaged: it does not open with its key.')
  }
}
const tick = (onProgress, done, total) => onProgress?.({ done, total })

/** local → local encrypted: each file sealed under a key of its own, into the sealed place. Returns `{ id: key }`. */
export async function sealFiles(ledger, rows, onProgress) {
  const keys = {}
  for (const [i, row] of rows.entries()) {
    const blob = await device().get(ledger, row.id)
    if (blob) {
      const plain = await checked(await bytesOf(blob), row)
      const key = c.newKey()
      await device().put(sealedSpace(ledger), row.id, new Blob([await c.seal(key, plain, fileAad(ledger, row.id))]))
      keys[row.id] = c.toBase64(key)
    }
    tick(onProgress, i + 1, rows.length)
  }
  return keys
}

/** local encrypted → local: each sealed file opened, into the readable place. */
export async function openFiles(ledger, rows, onProgress) {
  for (const [i, row] of rows.entries()) {
    const blob = (await device().get(sealedSpace(ledger), row.id)) ?? (await device().get(ledger, row.id))
    if (blob && row.file_key) await device().put(ledger, row.id, new Blob([await opened(await bytesOf(blob), row, ledger)], { type: row.type }))
    tick(onProgress, i + 1, rows.length)
  }
}

/**
 * Up: each file in a place on the device to the org's files on the server, as
 * it is (readable, or sealed), and checked there by size.
 */
export async function uploadFiles(from, org, rows, onProgress) {
  const sent = {}
  for (const [i, row] of rows.entries()) {
    const blob = await device().get(from, row.id)
    if (blob) {
      await server().put(org, row.id, blob)
      sent[row.id] = blob.size
    }
    tick(onProgress, i + 1, rows.length)
  }
  const there = new Map((await server().list(org)).map((f) => [f.id, f.size]))
  for (const [id, size] of Object.entries(sent)) {
    if (there.get(id) !== size) throw fail('attachment-damaged', 'A file did not arrive whole on the server.')
  }
}

/**
 * Down: each of the org's files from the server (or a copy kept here, `cache`)
 * into a place on the device — readable ones checked against their hash,
 * sealed ones (`sealed`) by opening them, and kept sealed.
 */
export async function downloadFiles(org, to, rows, { sealed = false, cache = null } = {}, onProgress) {
  for (const [i, row] of rows.entries()) {
    const blob = (cache && (await device().get(cache, row.id))) || (await server().get(org, row.id))
    if (blob) {
      const bytes = await bytesOf(blob)
      if (sealed) await opened(bytes, row, org)
      else await checked(bytes, row)
      await device().put(to, row.id, new Blob([bytes]))
    }
    tick(onProgress, i + 1, rows.length)
  }
}

/**
 * Cloud → cloud encrypted, in place: each readable file fetched, sealed under a
 * key of its own and kept here (the org's place), to replace it on the server
 * once the org is encrypted — the move queues them then (`queueUpload`), not
 * before: sent sooner, a readable ledger's reader would get a sealed file.
 * Returns `{ id: key }` for the rows.
 */
export async function sealForVault(org, rows, onProgress) {
  const keys = {}
  for (const [i, row] of rows.entries()) {
    const blob = await server().get(org, row.id)
    if (blob) {
      const plain = await checked(await bytesOf(blob), row)
      const key = c.newKey()
      await device().put(org, row.id, new Blob([await c.seal(key, plain, fileAad(org, row.id))]))
      keys[row.id] = c.toBase64(key)
    }
    tick(onProgress, i + 1, rows.length)
  }
  return keys
}

/**
 * Cloud encrypted → cloud, in place: each sealed file opened here, to replace
 * it on the server, readable, once the org is decrypted (the move queues them
 * then). They wait in a place of their own, `plain:<org>`, until sent.
 * Returns the ids.
 */
export async function openForCloud(org, rows, onProgress) {
  const staged = []
  for (const [i, row] of rows.entries()) {
    const blob = (await device().get(org, row.id)) || (await server().get(org, row.id))
    if (blob && row.file_key) {
      await device().put(`plain:${org}`, row.id, new Blob([await opened(await bytesOf(blob), row, org)], { type: row.type }))
      staged.push(row.id)
    }
    tick(onProgress, i + 1, rows.length)
  }
  return staged
}

/** Every file of the open ledger, readable, for a backup: `[{ id, bytes }]`, and the ids not here. */
export async function filesForBackup(onProgress) {
  const rows = await fileRows()
  const out = []
  const missing = []
  for (const [i, row] of rows.entries()) {
    try {
      out.push({ id: row.id, bytes: await bytesOf(await fileOf(row)) })
    } catch {
      missing.push(row.id)
    }
    tick(onProgress, i + 1, rows.length)
  }
  return { files: out, missing }
}

/**
 * After a backup is restored: its files put where the open ledger keeps them —
 * on this device, sealed under new keys for a ledger encrypted here, or on the
 * server — each checked against its row. Returns how many were put back.
 */
export async function restoreFiles(files, onProgress) {
  const byId = new Map(files.map((f) => [f.id, f.bytes]))
  const rows = await fileRows()
  let put = 0
  for (const [i, row] of rows.entries()) {
    const bytes = byId.get(row.id)
    if (bytes && (await sha256Hex(bytes)) === row.sha256) {
      if (where() === 'sealed') {
        const key = c.newKey()
        await device().put(space(), row.id, new Blob([await c.seal(key, bytes, fileAad(ledgerId(), row.id))]))
        await run('UPDATE attachments SET file_key = ? WHERE id = ?', [c.toBase64(key), row.id])
      } else await store().put(ledgerId(), row.id, new Blob([bytes], { type: row.type }))
      put++
    }
    tick(onProgress, i + 1, rows.length)
  }
  return put
}

/** Copies files from one place on the device to another, as they are. */
export async function copyPlace(from, to, rows) {
  for (const row of rows) {
    const blob = await device().get(from, row.id)
    if (blob) await device().put(to, row.id, blob)
  }
}
